import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiClient } from '../../services/http.js';
import { usePreferences } from '../../hooks/usePreferences.jsx';
import { useToast } from '../../hooks/useToast.jsx';
import { useNavigate } from 'react-router-dom';
import { markdownToSpeech } from '../../services/markdownToSpeech.js';
import ReportMessageModal from './ReportMessageModal.jsx';

/* ─── TTS hook ─── */
const useTTS = () => {
  const [speaking, setSpeaking] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const utterRef = useRef(null);

  const speak = useCallback((id, text, { rate = 1, pitch = 1, voiceURI = '' } = {}) => {
    window.speechSynthesis.cancel();
    const cleanText = markdownToSpeech(text);
    const u = new SpeechSynthesisUtterance(cleanText);
    u.lang = 'pt-BR';
    u.rate = rate;
    u.pitch = pitch;
    if (voiceURI) {
      const voices = window.speechSynthesis.getVoices();
      const v = voices.find(vv => vv.voiceURI === voiceURI);
      if (v) u.voice = v;
    }
    u.onstart = () => { setSpeaking(true); setCurrentId(id); };
    u.onend = () => { setSpeaking(false); setCurrentId(null); };
    u.onerror = () => { setSpeaking(false); setCurrentId(null); };
    utterRef.current = u;
    window.speechSynthesis.speak(u);
  }, []);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setCurrentId(null);
  }, []);

  return { speaking, currentId, speak, stop };
};

const FONT_CLASSES = {
  'P': 'text-sm',
  'M': 'text-[15px]',
  'G': 'text-lg',
};

/* ─── Assistant Message ─── */
const AssistantMessage = ({ msg, tts, preferences, conversationId, isLast, onRegenerate }) => {
  const [copied, setCopied] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [favoriting, setFavoriting] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reported, setReported] = useState(false);
  const [reporting, setReporting] = useState(false);
  const { showToast, showApiErrorToast } = useToast();
  const navigate = useNavigate();
  const isPlaying = tts.currentId === msg.id;

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const SOURCE_LABELS = useMemo(() => ({
    biblia: 'Bíblia Sagrada',
    catecismo: 'Catecismo da Igreja Católica',
    oracao: 'Orações',
    apoftegma: 'Apoftegmas',
    documento_magisterio: 'Documento do Magistério',
    liturgia: 'Liturgia Diária',
    santo: 'Santo do Dia',
    noticia_papa: 'Notícias do Papa',
    outro: 'Outras Fontes'
  }), []);

  const groupedCitations = useMemo(() => {
    if (!msg.citations) return {};
    return msg.citations.reduce((acc, cite) => {
      const type = cite.source_type || 'outro';
      if (!acc[type]) acc[type] = [];
      acc[type].push(cite);
      return acc;
    }, {});
  }, [msg.citations]);

  const citationsCount = msg.citations?.length || 0;

  const handleFavorite = async () => {
    if (favorited || favoriting) return;
    setFavoriting(true);
    try {
      const { res, data } = await apiClient.request('/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: msg.content,
          note_type: 'favorito',
          metadata_json: { conversation_id: conversationId, message_id: msg.id },
        }),
      });
      if (res.ok) {
        setFavorited(true);
        showToast({
          type: 'success',
          message: 'Salvo no Caderno Espiritual',
          actionLabel: 'Ver caderno',
          onAction: () => navigate('/caderno')
        });
      } else {
        showApiErrorToast(res, data, 'Erro ao favoritar.', { onRetry: handleFavorite });
      }
    } catch {
      showApiErrorToast(null, null, 'Erro de conexão.', { onRetry: handleFavorite });
    }
    setFavoriting(false);
  };

  const handleReport = async (reason) => {
    if (reporting) return;
    setReporting(true);
    try {
      const { res, data } = await apiClient.request(`/chat/${conversationId}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // msg.id só é o id numérico do banco depois que a conversa é
          // recarregada; uma resposta recém-gerada por streaming ainda tem
          // um UUID local — nesse caso o backend usa o snapshot enviado aqui.
          message_id: typeof msg.id === 'number' ? msg.id : null,
          content: msg.content,
          reason,
        }),
      });
      if (res.ok) {
        setReported(true);
        setShowReportModal(false);
        showToast({ type: 'success', message: 'Denúncia enviada. Obrigado por ajudar a melhorar o CatholicGPT.' });
      } else {
        showApiErrorToast(res, data, 'Erro ao enviar denúncia.');
      }
    } catch {
      showApiErrorToast(null, null, 'Erro de conexão.');
    }
    setReporting(false);
  };

  const handleTTS = () => {
    if (isPlaying) {
      tts.stop();
    } else {
      tts.speak(msg.id, msg.content, { rate: preferences.speed, pitch: preferences.intonation, voiceURI: preferences.voice });
    }
  };

  return (
    <div className="flex w-full justify-start gap-3">
      {/* Agent icon */}
      <div className="w-8 h-8 rounded-full bg-surface-container-high border border-primary/20 flex-shrink-0 flex items-center justify-center mt-1 shadow-sm overflow-hidden">
        <img src="/logo-64.webp" alt="CatholicGPT" className="w-6 h-6 object-contain rounded-full" />
      </div>

      <div className="max-w-[85%] md:max-w-[75%] space-y-3 flex-1 min-w-0">
        {/* Message content */}
        <div className={`text-on-surface-variant leading-relaxed chat-prose ${FONT_CLASSES[preferences.fontSize] || 'text-[15px]'}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
          >
            {msg.content || ''}
          </ReactMarkdown>
        </div>

        {/* Citations */}
        {citationsCount > 0 && (
          <details className="bg-surface-container-low border-l-2 border-primary/40 pl-4 py-2 space-y-2 mt-2 rounded-r-md cursor-pointer group">
            <summary className="font-semibold text-xs uppercase tracking-wider text-on-surface-variant/70 outline-none hover:text-primary transition-colors">
              {citationsCount} {citationsCount === 1 ? 'fonte consultada' : 'fontes consultadas'}
            </summary>
            
            <div className="pt-2 space-y-4 cursor-auto">
              {Object.entries(groupedCitations).map(([sourceType, cites]) => (
                <div key={sourceType} className="space-y-1.5">
                  <h4 className="text-xs font-semibold text-on-surface-variant/80 border-b border-outline-variant/30 pb-1">
                    {SOURCE_LABELS[sourceType] || SOURCE_LABELS.outro}
                  </h4>
                  {cites.map((cite, j) => (
                    <div key={j} className="text-sm text-on-surface-variant/80">
                      <p className="font-serif italic">
                        <span className="font-semibold not-italic text-on-surface-variant">{cite.reference || cite.title}</span>
                        {cite.snippet ? `: "${cite.snippet}"` : ''}
                      </p>
                      {cite.url && (
                        <a
                          href={cite.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline block mt-0.5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {cite.title}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Action bar */}
        {msg.content && (
          <div className="flex items-center gap-1 relative">
            {/* TTS play/pause */}
            <button
              onClick={handleTTS}
              title={isPlaying ? 'Parar leitura' : 'Ouvir resposta'}
              className={`min-h-[44px] min-w-[44px] flex items-center justify-center p-1.5 rounded-lg text-xs transition-colors ${isPlaying ? 'text-primary bg-primary/10' : 'text-on-surface-variant/50 hover:text-primary hover:bg-primary/5'}`}
            >
              <span className="material-symbols-outlined text-[18px]">{isPlaying ? 'stop_circle' : 'volume_up'}</span>
            </button>

            {/* TTS settings info */}
            <div className="group/tts relative">
              <button
                title="Configurações de leitura movidas para Preferências"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center p-1.5 rounded-lg text-on-surface-variant/50 hover:text-primary hover:bg-primary/5 transition-colors cursor-help"
              >
                <span className="material-symbols-outlined text-[16px]">tune</span>
              </button>
              <div className="absolute bottom-full mb-1 w-48 text-center hidden group-hover/tts:block bg-surface-container text-on-surface text-xs rounded p-1 shadow">
                Para ajustar voz e velocidade, acesse "Configurações" &gt; "Preferências"
              </div>
            </div>

            {/* Favoritar no Caderno Espiritual */}
            <button
              onClick={handleFavorite}
              disabled={favoriting}
              title={favorited ? 'Salva no Caderno Espiritual' : 'Salvar no Caderno Espiritual'}
              className={`min-h-[44px] min-w-[44px] flex items-center justify-center p-1.5 rounded-lg text-xs transition-colors ${favorited ? 'text-primary' : 'text-on-surface-variant/50 hover:text-primary hover:bg-primary/5'}`}
            >
              <span className="material-symbols-outlined text-[18px]">{favorited ? 'bookmark' : 'bookmark_border'}</span>
            </button>

            {/* Copy */}
            <button
              onClick={handleCopy}
              title="Copiar resposta"
              className={`min-h-[44px] min-w-[44px] flex items-center justify-center p-1.5 rounded-lg text-xs transition-colors ml-1 ${copied ? 'text-primary' : 'text-on-surface-variant/50 hover:text-primary hover:bg-primary/5'}`}
            >
              <span className="material-symbols-outlined text-[18px]">{copied ? 'check' : 'content_copy'}</span>
            </button>

            {/* Regenerar - só na última resposta */}
            {isLast && onRegenerate && (
              <button
                onClick={onRegenerate}
                title="Gerar nova resposta (consome 1 crédito)"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center p-1.5 rounded-lg text-xs text-on-surface-variant/50 hover:text-primary hover:bg-primary/5 transition-colors"
              >
                <span className="material-symbols-outlined text-[18px]">refresh</span>
              </button>
            )}

            {/* Reportar resposta — Guideline 1.2 da Apple exige uma via de
                denúncia visível para conteúdo gerado dinamicamente por IA. */}
            <button
              onClick={() => setShowReportModal(true)}
              disabled={reported}
              title={reported ? 'Denúncia enviada' : 'Reportar esta resposta'}
              className={`min-h-[44px] min-w-[44px] flex items-center justify-center p-1.5 rounded-lg text-xs transition-colors ${reported ? 'text-primary' : 'text-on-surface-variant/50 hover:text-error hover:bg-error/5'}`}
            >
              <span className="material-symbols-outlined text-[18px]">{reported ? 'flag' : 'outlined_flag'}</span>
            </button>
          </div>
        )}
      </div>

      {showReportModal && (
        <ReportMessageModal
          submitting={reporting}
          onSubmit={handleReport}
          onCancel={() => setShowReportModal(false)}
        />
      )}
    </div>
  );
};

/* ─── User Message ─── */
const UserMessage = ({ msg, isLast, onEdit, onDelete, preferences }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.content);

  const submitEdit = () => {
    if (draft.trim() && draft.trim() !== msg.content) {
      onEdit(draft.trim());
    }
    setEditing(false);
  };

  return (
    <div className="flex w-full justify-end gap-2 group">
      <div className="max-w-[80%] md:max-w-[70%]">
        {editing ? (
          <div className="glass-panel ghost-border-tl rounded-2xl rounded-tr-sm p-3 flex flex-col gap-2">
            <textarea
              autoFocus
              rows={3}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEdit(); } }}
              className="bg-transparent border-none text-on-surface text-base leading-relaxed focus:ring-0 focus:outline-none resize-none w-full"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditing(false)} className="text-xs text-on-surface-variant hover:text-on-surface px-3 py-1 rounded-full border border-outline-variant/30">Cancelar</button>
              <button onClick={submitEdit} className="text-xs bg-primary text-on-primary px-3 py-1 rounded-full">Salvar</button>
            </div>
          </div>
        ) : (
          <div className="glass-panel ghost-border-tl rounded-2xl rounded-tr-sm p-4 shadow-[0px_8px_24px_rgba(5,7,10,0.3)]">
            <p className={`font-body text-on-surface leading-relaxed whitespace-pre-wrap ${FONT_CLASSES[preferences.fontSize] || 'text-[15px]'}`}>{msg.content}</p>
          </div>
        )}

        {/* Edit/delete - only last user message */}
        {isLast && !editing && (
          <div className="flex gap-1 justify-end mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => { setDraft(msg.content); setEditing(true); }} title="Editar mensagem" className="min-h-[44px] min-w-[44px] flex items-center justify-center p-1 text-on-surface-variant/50 hover:text-primary transition-colors">
              <span className="material-symbols-outlined text-[16px]">edit</span>
            </button>
            <button onClick={onDelete} title="Excluir mensagem" className="min-h-[44px] min-w-[44px] flex items-center justify-center p-1 text-on-surface-variant/50 hover:text-error transition-colors">
              <span className="material-symbols-outlined text-[16px]">delete</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main MessageList ─── */
export const MessageList = ({ messages, loading, onEditLastMessage, onDeleteLastMessage, onRegenerateLastMessage, conversationId }) => {
  const endRef = useRef(null);
  const tts = useTTS();
  const { preferences } = usePreferences();

  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    const container = endRef.current?.closest('.overflow-y-auto');
    if (!container) return;
    const handleScroll = () => {
      const isNearBottom = (container.scrollHeight - container.scrollTop - container.clientHeight) < 120;
      setShowScrollButton(!isNearBottom);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const container = endRef.current?.closest('.overflow-y-auto');
    if (!container) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const isNearBottom = (container.scrollHeight - container.scrollTop - container.clientHeight) < 120;
    // Auto-scroll se estiver perto do fim ou for mensagem inicial
    if (isNearBottom || messages.length <= 2) {
      endRef.current?.scrollIntoView({ behavior: loading ? 'auto' : 'smooth' });
    }
  }, [messages, loading]);

  const scrollToBottom = () => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const lastUserIdx = messages.reduce((last, m, i) => (m.role === 'user' ? i : last), -1);
  const lastAssistantIdx = messages.reduce((last, m, i) => (m.role === 'assistant' ? i : last), -1);

  return (
    <div 
      className="flex-1 w-full flex flex-col space-y-8"
      aria-live="polite"
      aria-busy={loading}
    >
      {messages.map((msg, i) =>
        msg.role === 'user' ? (
          <UserMessage
            key={msg.id || i}
            msg={msg}
            isLast={i === lastUserIdx}
            onEdit={(text) => onEditLastMessage?.(text)}
            onDelete={() => onDeleteLastMessage?.()}
            preferences={preferences}
          />
        ) : (
          <AssistantMessage
            key={msg.id || i}
            msg={msg}
            tts={tts}
            preferences={preferences}
            conversationId={conversationId}
            isLast={i === lastAssistantIdx}
            onRegenerate={onRegenerateLastMessage}
          />
        )
      )}

      {loading && (
        <div className="flex items-center gap-3 text-on-surface-variant/60">
          <div className="w-8 h-8 rounded-full bg-surface-container-high border border-primary/20 flex-shrink-0 flex items-center justify-center overflow-hidden">
            <img src="/logo-64.webp" alt="" className="w-6 h-6 object-contain rounded-full animate-pulse" />
          </div>
          <span className="font-body italic text-sm">Refletindo sobre a sabedoria...</span>
        </div>
      )}
      <div ref={endRef} />
      
      {showScrollButton && (
        <button 
          onClick={scrollToBottom}
          className="fixed bottom-32 md:bottom-28 right-1/2 translate-x-[40vw] md:translate-x-[35vw] xl:translate-x-[400px] w-10 h-10 flex items-center justify-center bg-surface-container-highest hover:bg-surface-variant text-on-surface rounded-full shadow-lg border border-outline-variant/30 z-50 transition-all"
          title="Ir para o fim"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_downward</span>
        </button>
      )}
    </div>
  );
};
